import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';

const ChatbotCapabilities = () => {
  const tiers = [
    {
      name: "Básico",
      color: "blue",
      cost: "5 créditos",
      plans: ["Free", "Basic"],
      models: ["GPT-3.5 Turbo"],
      languages: 3,
      languageList: ["Español", "Inglés", "Portugués"],
      capabilities: [
        "Consultas rápidas",
        "Análisis básicos",
        "Investigación simple",
        "Respuestas directas",
        "Ejemplos simples"
      ],
      limitations: [
        "Sin generación de código",
        "Sin análisis de datos",
        "Sin exportación de archivos",
        "Contexto limitado (4K tokens)"
      ],
      examples: [
        {
          question: "¿Qué es Python?",
          response: "Respuesta directa y concisa sobre Python"
        },
        {
          question: "Dame 5 ideas para un blog",
          response: "Lista simple de 5 ideas"
        }
      ]
    },
    {
      name: "Estándar",
      color: "green",
      cost: "15 créditos",
      plans: ["Standard"],
      models: ["GPT-4", "Claude 3.5 Sonnet"],
      languages: 4,
      languageList: ["Español", "Inglés", "Portugués", "Francés"],
      capabilities: [
        "Consultas con contexto profundo",
        "Respuestas elaboradas con datos",
        "Análisis moderado",
        "Gráficos y visualizaciones básicas",
        "Investigación con fuentes",
        "Comparativas y tablas",
        "Generación de código básico"
      ],
      features: [
        "✅ Código básico (snippets, ejemplos)",
        "✅ Análisis de datos moderado",
        "✅ Gráficos y tablas",
        "✅ Exportación en texto",
        "✅ Contexto extendido (32K tokens)"
      ],
      examples: [
        {
          question: "Analiza ventajas de React vs Vue",
          response: "Tabla comparativa con pros/cons de cada framework"
        },
        {
          question: "Crea gráfico de ventas mensuales",
          response: "Gráfico de barras con datos y análisis"
        }
      ]
    },
    {
      name: "Avanzado",
      color: "purple",
      cost: "30 créditos",
      plans: ["Professional", "Premium", "Elite"],
      models: ["GPT-4", "Claude 3.5", "Gemini Pro", "DeepSeek"],
      languages: 5,
      languageList: ["Español", "Inglés", "Portugués", "Francés", "Alemán"],
      badge: "⭐ CERTIFICADO",
      capabilities: [
        "Creación de código completo y optimizado",
        "Análisis profundo de datos complejos",
        "Investigación exhaustiva multi-fuente",
        "Métricas y KPIs avanzados",
        "Generación de tablas Excel complejas",
        "Documentos Word/PDF profesionales",
        "Gráficos avanzados (múltiples tipos)",
        "Certificación de calidad de respuesta"
      ],
      features: [
        "✅ Código producción + tests + documentación",
        "✅ Análisis profundo con métricas",
        "✅ Gráficos avanzados (líneas, barras, pie, scatter)",
        "✅ Exportación: Excel, Word, PDF, CSV",
        "✅ Investigación profunda con referencias",
        "✅ Contexto máximo (128K tokens)",
        "✅ CERTIFICACIÓN DE CALIDAD"
      ],
      certification: {
        enabled: true,
        includes: [
          "Verificación de precisión",
          "Validación de fuentes",
          "Score de confiabilidad (0-100)",
          "Referencias bibliográficas",
          "Timestamp de generación",
          "Revisión de calidad automática"
        ]
      },
      examples: [
        {
          question: "Crea API REST en FastAPI con JWT",
          response: "Código completo + tests + documentación + deployment guide"
        },
        {
          question: "Analiza 1000 ventas y genera Excel",
          response: "Excel con múltiples hojas, gráficos, tablas dinámicas y análisis"
        },
        {
          question: "Informe competencia sector fintech",
          response: "PDF profesional con investigación, gráficos, métricas y conclusiones"
        }
      ]
    }
  ];

  const getColorClasses = (color) => {
    const colors = {
      blue: {
        bg: 'bg-blue-50 dark:bg-blue-900/20',
        border: 'border-blue-200 dark:border-blue-700',
        text: 'text-blue-600 dark:text-blue-400',
        badge: 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300'
      },
      green: {
        bg: 'bg-green-50 dark:bg-green-900/20',
        border: 'border-green-200 dark:border-green-700',
        text: 'text-green-600 dark:text-green-400',
        badge: 'bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300'
      },
      purple: {
        bg: 'bg-purple-50 dark:bg-purple-900/20',
        border: 'border-purple-200 dark:border-purple-700',
        text: 'text-purple-600 dark:text-purple-400',
        badge: 'bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-300'
      }
    };
    return colors[color] || colors.blue;
  };

  return (
    <div className="font-display bg-background-light dark:bg-background-dark min-h-screen">
      <div className="px-4 sm:px-10 md:px-20 lg:px-40">
        <Header />
        
        <div className="py-12">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-black text-gray-900 dark:text-white mb-4">
              🤖 Capacidades del Chatbot Automatizado
            </h1>
            <p className="text-lg text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              Tres niveles de inteligencia artificial adaptados a tus necesidades. 
              Desde consultas rápidas hasta análisis profesionales certificados.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
            {tiers.map((tier, index) => {
              const colorClasses = getColorClasses(tier.color);
              
              return (
                <div 
                  key={index}
                  className={`${colorClasses.bg} ${colorClasses.border} border-2 rounded-2xl p-6 relative`}
                >
                  {tier.badge && (
                    <div className="absolute -top-3 right-6 bg-yellow-400 text-yellow-900 px-4 py-1 rounded-full text-sm font-bold">
                      {tier.badge}
                    </div>
                  )}
                  
                  <div className="text-center mb-6">
                    <h2 className={`text-2xl font-bold ${colorClasses.text} mb-2`}>
                      {tier.name}
                    </h2>
                    <p className="text-gray-600 dark:text-gray-400 text-sm mb-1">
                      {tier.cost} por consulta
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-500">
                      Planes: {tier.plans.join(", ")}
                    </p>
                  </div>

                  <div className="space-y-4 mb-6">
                    <div>
                      <h3 className="font-bold text-gray-900 dark:text-white mb-2">
                        🧠 Modelos IA:
                      </h3>
                      <div className="flex flex-wrap gap-2">
                        {tier.models.map((model, i) => (
                          <span key={i} className={`${colorClasses.badge} px-2 py-1 rounded text-xs`}>
                            {model}
                          </span>
                        ))}
                      </div>
                    </div>

                    <div>
                      <h3 className="font-bold text-gray-900 dark:text-white mb-2">
                        🌍 Idiomas: {tier.languages}
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {tier.languageList.join(", ")}
                      </p>
                    </div>

                    <div>
                      <h3 className="font-bold text-gray-900 dark:text-white mb-2">
                        ✨ Capacidades:
                      </h3>
                      <ul className="space-y-1">
                        {tier.capabilities.map((cap, i) => (
                          <li key={i} className="text-sm text-gray-700 dark:text-gray-300 flex items-start">
                            <span className="mr-2">•</span>
                            {cap}
                          </li>
                        ))}
                      </ul>
                    </div>

                    {tier.features && (
                      <div>
                        <h3 className="font-bold text-gray-900 dark:text-white mb-2">
                          🔧 Características:
                        </h3>
                        <ul className="space-y-1">
                          {tier.features.map((feature, i) => (
                            <li key={i} className="text-sm text-gray-700 dark:text-gray-300">
                              {feature}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {tier.certification && (
                      <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg p-3">
                        <h3 className="font-bold text-yellow-900 dark:text-yellow-300 mb-2 flex items-center">
                          <span className="mr-2">🏆</span>
                          Certificación de Calidad
                        </h3>
                        <ul className="space-y-1">
                          {tier.certification.includes.map((item, i) => (
                            <li key={i} className="text-xs text-yellow-800 dark:text-yellow-300">
                              ✓ {item}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>

                  <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                    <h3 className="font-bold text-gray-900 dark:text-white mb-3 text-sm">
                      📝 Ejemplos de Uso:
                    </h3>
                    {tier.examples.map((example, i) => (
                      <div key={i} className="mb-3 last:mb-0">
                        <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                          "{example.question}"
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 italic">
                          → {example.response}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="text-center">
            <Link
              to="/autochat"
              className="inline-block px-8 py-4 bg-primary hover:bg-primary-dark text-white font-bold rounded-xl transition-colors"
            >
              Probar Chatbot Automatizado →
            </Link>
          </div>
        </div>
        
        <Footer />
      </div>
    </div>
  );
};

export default ChatbotCapabilities;
